import type { DeviceFacts, JobView, RunbookSummary } from '@api/devices/contracts';
import { runbookUnavailableReason } from '@api/operations/runbook-compatibility';
import { Play, ShieldCheck, X } from 'lucide-react';
import { Dialog } from 'radix-ui';
import { useEffect, useState } from 'react';
import { type DeviceRef, JobOutput, useCapabilities, useJobActions } from '../operations/jobs';
import { JobStateBadge, ReviewList } from '../operations/meta';
import { useJob } from '../search/hooks';
import { Button } from '../ui/Button';
import { reviewScript } from '@api/operations/script-review';

const disruptionLabel = { none: 'No interruption expected', brief: 'May briefly interrupt the user', restart: 'Restarts the device' };

/** Run a reviewed runbook: read-only ones run now and show their result here; changes go to approval. */
export function RunbookDialog({ runbook, device, agent, reason: initialReason, open, onOpenChange, onNeedsApproval }: {
  runbook: RunbookSummary | null; device: DeviceRef; agent?: DeviceFacts['agent']; reason?: string; open: boolean; onOpenChange: (open: boolean) => void;
  /** A change runbook hands off to the approval surface, owned by the page so it outlives this dialog. */
  onNeedsApproval: (job: JobView) => void;
}) {
  const { create } = useJobActions();
  const { approve: canApprove } = useCapabilities(device);
  const [params, setParams] = useState<Record<string, number | string>>({});
  const [reason, setReason] = useState('');
  const [jobId, setJobId] = useState<string | null>(null);
  const job = useJob(jobId);
  useEffect(() => {
    if (!open || !runbook) return;
    setParams(Object.fromEntries(runbook.parameters.map(p => [p.name, p.default ?? ''])));
    setReason(initialReason ?? runbook.title); setJobId(null);
  }, [open, runbook, initialReason]);
  if (!runbook) return null;
  const unavailable = runbookUnavailableReason(runbook, agent);
  const submit = () => !unavailable && create.mutate({ deviceId: device.id, reason: reason.trim() || runbook.title, runbook: { id: runbook.id, parameters: params } }, {
    onSuccess: created => {
      setJobId(created.id);
      if (created.state === 'awaiting_approval' && canApprove) { onOpenChange(false); onNeedsApproval(created); }
    },
  });
  const current = job.data;
  const parametersValid = runbook.parameters.every(p => {
    const value = params[p.name];
    return p.type === 'integer'
      ? typeof value === 'number' && Number.isSafeInteger(value) && value >= p.min && value <= p.max
      : typeof value === 'string' && value.length >= p.minLength && value.length <= p.maxLength && value.trim() === value
        && !/[\p{Cc}\p{Cf}\p{Cs}]/u.test(value) && (!p.pattern || new RegExp(p.pattern).test(value));
  });
  return (
      <Dialog.Root open={open} onOpenChange={onOpenChange}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-50 bg-black/40 animate-fade" />
          <Dialog.Content className="fixed inset-x-0 bottom-0 z-50 max-h-[90dvh] overflow-y-auto rounded-t-2xl border border-line bg-raised p-5 shadow-pop animate-pop sm:inset-auto sm:left-1/2 sm:top-[10vh] sm:w-[min(640px,calc(100vw-2rem))] sm:-translate-x-1/2 sm:rounded-2xl">
            <div className="flex items-start gap-3">
              <div className="min-w-0 flex-1">
                <Dialog.Title className="text-[17px] font-semibold">{runbook.title}</Dialog.Title>
                <Dialog.Description className="mt-0.5 text-[13px] text-ink-2">{runbook.summary}</Dialog.Description>
              </div>
              <Dialog.Close className="rounded-md p-1 text-ink-3 hover:bg-hover" aria-label="Close"><X className="size-4" /></Dialog.Close>
            </div>
            <div className="mt-3 flex flex-wrap gap-1.5 text-[12px]">
              <span className="rounded-md bg-sunken px-2 py-0.5 font-mono text-ink-2">{device.title}</span>
              <span className={`rounded-md px-2 py-0.5 ${runbook.effect === 'read' ? 'bg-healthy-soft text-healthy' : 'bg-attention-soft text-attention'}`}>{runbook.effect === 'read' ? 'Read-only' : 'Changes the device · needs approval'}</span>
              <span className="rounded-md bg-sunken px-2 py-0.5 text-ink-2">{disruptionLabel[runbook.disruption]}</span>
              <span className="rounded-md bg-sunken px-2 py-0.5 text-ink-2">Runs as SYSTEM · v{runbook.version}</span>
            </div>
            {!current ? (
              <div className="mt-4 space-y-3">
                {runbook.parameters.map(p => (
                  <label key={p.name} className="flex items-center justify-between gap-3 text-[13.5px]">
                    <span className="text-ink-2">{p.label}</span>
                    {p.type === 'integer'
                      ? <input type="number" min={p.min} max={p.max} value={params[p.name] ?? p.default} onChange={e => setParams(v => ({ ...v, [p.name]: Number(e.target.value) }))}
                          className="h-9 w-24 rounded-lg border border-line bg-panel px-2 text-right tabular outline-none focus:border-brand/50" />
                      : <input type="text" required minLength={p.minLength} maxLength={p.maxLength} pattern={p.pattern} value={params[p.name] ?? ''}
                          onChange={e => setParams(v => ({ ...v, [p.name]: e.target.value }))}
                          className="h-9 w-64 rounded-lg border border-line bg-panel px-2 outline-none focus:border-brand/50" />}

                  </label>
                ))}
                <label className="block text-[13.5px]"><span className="text-ink-2">Reason</span>
                  <input value={reason} onChange={e => setReason(e.target.value)} maxLength={500}
                    className="mt-1 h-9 w-full rounded-lg border border-line bg-panel px-3 outline-none focus:border-brand/50" /></label>
                <details className="rounded-lg border border-line px-3 py-2 text-[13px]">
                  <summary className="cursor-pointer font-medium text-ink-2">What it runs</summary>
                  <div className="mt-2"><ReviewList findings={reviewScript(runbook.script)} compact /></div>
                  <pre className="mt-2 max-h-56 overflow-auto rounded-lg bg-sunken p-2.5 font-mono text-[12px]">{runbook.script}</pre>
                  <p className="mt-1.5 text-[12px] text-ink-3">Verification: {runbook.verification}</p>
                </details>
                <div className="flex justify-end gap-2">
                  <Dialog.Close asChild><Button variant="ghost">Cancel</Button></Dialog.Close>
                  <Button variant="primary" disabled={create.isPending || !parametersValid || !!unavailable} onClick={submit}>
                    {runbook.effect === 'read' ? <><Play className="size-4" />Run now</> : <><ShieldCheck className="size-4" />Request approval</>}
                  </Button>
                </div>
                {unavailable && <p role="status" className="mt-2 text-[13px] text-attention">{unavailable}</p>}
              </div>
            ) : (
              <div className="mt-4 space-y-3">
                <div className="flex items-center gap-2"><JobStateBadge state={current.state} /><span className="text-[13px] text-ink-3">
                  {current.state === 'queued' ? 'Waiting for the device to collect it…' : current.state === 'awaiting_approval' ? 'Waiting for someone with approval rights.' : ''}</span></div>
                <JobOutput job={current} />
                <p className="text-[12px] text-ink-3">This also appears in the device’s Activity and in Work.</p>
              </div>
            )}
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
  );
}
